import React, { useEffect, useState, useCallback } from 'react';
import { useAlbumArrowKeys } from './useAlbumArrowKeys.js';
import { useParams, useNavigate } from 'react-router-dom';
import { imageApi } from '../../../api/imageApi.js';
import { albumApi } from '../../../api/albumApi.js';
import { loginApi } from '../../../api/loginApi.js';
import { AddToAlbumModal } from './addToAlbumModal.js';
import './albumScreenV2.css';

const ADD_TO_ALBUM_IDS = [7, 11];

const AlbumScreenV2 = () => {
    const { albumID } = useParams();
    const [imageList, setImageList] = useState([]);
    const [albumName, setAlbumName] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const navigate = useNavigate();

    const fetchAlbumImages = useCallback(async () => {
        try {
            const response = await albumApi.getPhotosInAlbum(albumID);
            if (response.status === false && response.msg === "Forbidden") {
                navigate('/login');
                return;
            }
            if (response && response.data && response.data.ids) {
                // Initialize images without loading them
                setImageList(response.data.ids);
            }
        } catch (error) {
            if (error.status === 403) {
                navigate('/login');
            } else {
                console.error('Error fetching the images:', error);
            }
        }
    }, [navigate, albumID]);

    useEffect(() => {
        fetchAlbumImages();
    }, [fetchAlbumImages]);

    useEffect(() => {
        const fetchLoginRoles = async () => {
            try {
                const response = await loginApi.loginRoles();
                if (response.status === false && response.msg === "Forbidden") {
                    navigate('/login');
                    return;
                }
                if (response && response.data) {
                    setIsAdmin(!!response.data.admin);
                }
            } catch (error) {
                if (error.status === 403) {
                    navigate('/login');
                } else {
                    console.error('Error fetching login roles:', error);
                }
            }
        };

        fetchLoginRoles();
    }, [navigate]);

    useEffect(() => {
        const fetchAlbumDetails = async () => {
            try {
                const response = await albumApi.getAlbumDetails(albumID);
                if (response.status === false && response.msg === "Forbidden") {
                    navigate('/login');
                    return;
                }
                if (response && response.data) {
                    setAlbumName(response.data.data.name)
                }
            } catch (error) {
                if (error.status === 403) {
                    navigate('/login');
                } else {
                    console.error('Error fetching the images:', error);
                }
            }
        };

        fetchAlbumDetails();
    }, [navigate, albumID]);

    // Method to load an image when it comes into the viewport
    const loadImage = async (img) => {
        const blob = await imageApi.getImageBytes(img.id);
        setImageList(prevImages => prevImages.map(image =>
            image.id === img.id ? {...img, src: blob} : image
        ));
    }

    // Intersection observer logic
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const imageObj = imageList.find(function (item) { return item.id === Number(entry.target.dataset.imgId) });
                    if (imageObj) {
                        loadImage(imageObj);
                    }
                }
            });
        });

        const images = document.querySelectorAll('.grid-images img');
        images.forEach(img => {
            if (!img.src || img.src === 'path_to_placeholder.png') {
                observer.observe(img);
            }
        });

        return () => observer.disconnect(); // Cleanup on component unmount
    }, [imageList]);

    const lastImageKey = `album-last-${albumID}`;

    const navigateByOffset = useCallback((direction) => {
        if (!imageList.length) return;

        const lastId = sessionStorage.getItem(lastImageKey);
        let index = lastId
            ? imageList.findIndex((img) => img.id === Number(lastId))
            : -1;

        if (direction > 0) {
            index = index < 0 ? 0 : Math.min(index + 1, imageList.length - 1);
        } else {
            index = index < 0 ? imageList.length - 1 : Math.max(index - 1, 0);
        }

        const image = imageList[index];
        if (!image) return;

        sessionStorage.setItem(lastImageKey, String(image.id));
        navigate(`/albums/${albumID}/${image.id}`);
    }, [imageList, albumID, navigate, lastImageKey]);

    const goToPrevImage = useCallback(() => navigateByOffset(-1), [navigateByOffset]);
    const goToNextImage = useCallback(() => navigateByOffset(1), [navigateByOffset]);

    useAlbumArrowKeys(goToPrevImage, goToNextImage);

    const handleImageClick = (image) => {
        sessionStorage.setItem(lastImageKey, String(image.id));
        navigate(`/albums/${albumID}/${image.id}`);
    };

    const handleLogout = async () => {
        loginApi.logout();
        navigate('/login');
    };

    const canAddToAlbum = isAdmin && ADD_TO_ALBUM_IDS.includes(Number(albumID));

    return (
        <div>
            <div className="header">
                <h1>Album {albumName}</h1>
                <div className="header-actions">
                    {canAddToAlbum && (
                        <button onClick={() => setShowAddModal(true)}>Add to Album</button>
                    )}
                    <button onClick={handleLogout}>Logout</button>
                </div>
            </div>
            {showAddModal && (
                <AddToAlbumModal
                    albumID={albumID}
                    onClose={() => setShowAddModal(false)}
                    onAdded={fetchAlbumImages}
                />
            )}
            <div className="grid-images">
                {imageList.map(image => (
                    <img
                        key={image.id}
                        data-img-id={image.id}
                        src={image.src}
                        alt={image.title}
                        onClick={() => handleImageClick(image)}
                    />
                ))}
            </div>
        </div>
    );
}

export { AlbumScreenV2 };